import type { PublicPlayer, ReactionKind } from '@durak/shared';
import clsx from 'clsx';
import { motion, AnimatePresence } from 'framer-motion';
import { ReactionBubble } from './Reactions';
import { Avatar } from './Avatar';
import { TurnCountdownRing } from './TurnCountdownRing';

interface Props {
  player: PublicPlayer;
  isAttacker?: boolean;
  isDefender?: boolean;
  isYou?: boolean;
  toAct?: boolean;
  rotation?: number; // degrees (visual)
  reaction?: { kind: ReactionKind; id: number } | null;
  /** When this seat is to-act, show a countdown ring against the server's
   *  move deadline. Null/undefined = no ring (e.g. local player — they
   *  already see the countdown chip on their own hand panel). */
  moveDeadline?: number | null;
}

export function Seat({ player, isAttacker, isDefender, isYou, toAct, rotation = 0, reaction, moveDeadline }: Props) {
  return (
    <motion.div
      animate={toAct
        ? { boxShadow: ['0 0 0 0 rgba(255,181,71,0.0)', '0 0 0 6px rgba(255,181,71,0.18)', '0 0 0 0 rgba(255,181,71,0.0)'] }
        : { boxShadow: '0 0 0 0 rgba(0,0,0,0)' }}
      transition={{ duration: 1.6, repeat: toAct ? Infinity : 0, ease: 'easeInOut' }}
      className={clsx(
        'panel relative px-2.5 py-1.5 flex items-center gap-2 select-none transition',
        toAct && 'ring-2 ring-accent',
        isYou && 'border-accent/60',
      )}
      style={rotation ? { transform: `rotate(${rotation}deg)` } : undefined}
    >
      <AnimatePresence>
        {reaction && <ReactionBubble key={reaction.id} id={reaction.id} kind={reaction.kind} />}
      </AnimatePresence>
      <div className="relative">
        <Avatar src={player.avatar} name={player.name} size={32} />
        <span
          className={clsx(
            'absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-ink-800',
            player.connected ? 'bg-emerald-400' : 'bg-zinc-500',
          )}
        />
        {toAct && moveDeadline && (
          <span className="absolute -top-1 -left-1">
            <TurnCountdownRing deadline={moveDeadline} size={20} inverted />
          </span>
        )}
      </div>
      <div className="min-w-0">
        <div className="text-xs sm:text-sm font-semibold truncate max-w-[10ch] leading-tight">
          {player.name}{isYou && <span className="text-accent text-[10px] ml-1">you</span>}
        </div>
        <div className="text-[10px] text-ink-200 flex items-center gap-1 mt-0.5">
          <span>{player.handCount}c</span>
          {isAttacker && <span className="px-1.5 py-0 rounded-full text-[9px] font-semibold tracking-wider border border-accent/40 text-accent-400">ATK</span>}
          {isDefender && <span className="px-1.5 py-0 rounded-full text-[9px] font-semibold tracking-wider border border-felt-300/50 text-felt-100">DEF</span>}
          {player.isBot && <span className="px-1.5 py-0 rounded-full text-[9px] font-semibold tracking-wider border border-ink-500 text-ink-200">BOT</span>}
          {player.isBot && toAct && (
            <span className="px-1.5 py-0 rounded-full text-[9px] font-semibold tracking-wider bg-accent/15 text-accent-300 animate-shimmer">thinking…</span>
          )}
          {player.finished && <span className="px-1.5 py-0 rounded-full text-[9px] font-semibold tracking-wider border border-emerald-500/40 text-emerald-300">OUT</span>}
        </div>
      </div>
    </motion.div>
  );
}
